import type { NestedFieldValueType } from '@helpcenter/field-types';
import type { NestedOption } from 'view/common/fields/cascading-select';
import type { Option } from '@atlaskit/select';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import FeatureGates from '@atlaskit/feature-gate-js-client';

/**
 *
 * The `children` property supports both `null` and `undefined` to handle different scenarios:
 * - The cascading select component returns `children: undefined` when no child option is present
 * - The cascading select component expects a value object with `children: null` for initialization
 * - This dual type support ensures compatibility across all usage patterns
 */
export interface CascadingSelectValueType {
    primaryValue: NestedOption;
    children: Option<string> | null | undefined;
}

// Types compatible with help-center CascadingSelect component
export type CascadingSelectPublicSetValue = null | CascadingSelectValueType;

export type CascadingSelectInputValue = {
    childId: string | null;
    parentId: string;
} | null;

export type CascadingSelectTransformedValue = {
    parent: {
        id: string;
        value: string;
    };
    child: {
        id: string;
        value: string;
    } | null;
} | null;

const findCascadingSelectLookupValues = (lookupData: NestedOption[], parentId: string, childId: string | null) => {
    const foundOption = lookupData.find((currentOption) => currentOption.value === parentId);
    if (!foundOption) {
        throw new FieldValueLookupError();
    }

    const childOption =
        childId !== null && foundOption.children
            ? foundOption.children.find((child) => child.value === childId) || null
            : null;

    const value = {
        parent: foundOption,
        children: childOption,
    };

    return value;
};

export const validateCascadingSelectSetValue = (value: CascadingSelectInputValue): boolean => {
    return (
        value === null ||
        (typeof value === 'object' &&
            'parentId' in value &&
            'childId' in value &&
            isNotEmptyString(value.parentId) &&
            (isNotEmptyString(value.childId) || value.childId === null))
    );
};

export const transformSetValueCascadingSelect = (
    value: CascadingSelectInputValue,
    lookupData: NestedOption[]
): CascadingSelectPublicSetValue => {
    if (value === null || value.parentId === null) {
        return null;
    }

    const cascadingSelectValue = findCascadingSelectLookupValues(lookupData, value.parentId, value.childId);

    return {
        primaryValue: cascadingSelectValue.parent,
        children: cascadingSelectValue.children,
    };
};

export const transformGetValueCascadingSelect = (
    cascadingValue: CascadingSelectValueType | null
): CascadingSelectTransformedValue => {
    if (!cascadingValue || (FeatureGates.checkGate('ditto_fix_getvalue_empty_state') && !cascadingValue.primaryValue)) {
        return null;
    }

    const { primaryValue, children } = cascadingValue;

    return {
        parent: {
            id: primaryValue.value,
            value: primaryValue.label,
        },
        child: children
            ? {
                  id: children.value,
                  value: children.label,
              }
            : null,
    };
};

export const normalizeCascadingSelectValue = (
    value: NestedFieldValueType | CascadingSelectValueType | null | undefined
): NestedFieldValueType => {
    if (!value) {
        return { primaryValue: null, children: null };
    }

    // If it's already NestedFieldValueType (string-based primaryValue)
    if (typeof value.primaryValue === 'string' || value.primaryValue === null) {
        return value as NestedFieldValueType;
    }

    // If it's object-based, extract string values
    const childrenValue = (value as CascadingSelectValueType).children?.value;
    return {
        primaryValue: value.primaryValue?.value || null,
        children: childrenValue || null,
    };
};

export const transformValueToBasicCascadingSelect = (value: CascadingSelectPublicSetValue): NestedFieldValueType => {
    if (!value) {
        return { primaryValue: null, children: null };
    }

    return {
        primaryValue: value.primaryValue?.value || null,
        children: value.children?.value || null,
    };
};
